import flatten from 'lodash/flatten';

import {
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_discount_info as ProductDiscountInfo,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_matching_catalog_product_info_discount_info as MatchingProductDiscountInfo,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_product_price_coupon_discount_info_list as ProductPriceCouponDiscountInfo,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_product_price_first_order_discount as FirstOrderDiscount,
  PdpCouponDiscountType,
  ProductAdditionalOptionInput as AdditionalOptionDetail,
} from '../graphql/select-option_types.2';
import { BogoInfo, CustomTextOption, SelectedItemMap } from '../types/option-type';

export const bpToDecimal = (bp: number) => {
  return bp / 10000;
};

export const getAdditionalOptionDetailList = (CustomTextOptions: CustomTextOption[][]) => {
  try {
    const reformCustomTextOptions = flatten(CustomTextOptions)
      .filter(({ selected_value }) => selected_value)
      .map<AdditionalOptionDetail>(({ name, selected_value: value }) => ({ name, value }));
    return reformCustomTextOptions;
  } catch (err) {
    return [];
  }
};
/**
 * BOGO 프로모션 할인가 계산
 * ex) 10000원을 3개로 분배 → 3333, 3333, 3334
 */
export const distributePrice = (totalPrice: number, quantity: number) => {
  const basePrice = Math.floor(totalPrice / quantity); // 기본 가격 (소수점 버림)
  const remainder = totalPrice % quantity; // 나머지

  const prices: number[] = [];

  // 모든 상품에 기본 가격 할당
  for (let i = 0; i < quantity; i++) {
    prices.push(basePrice);
  }

  // 마지막 상품에 나머지 추가
  if (remainder > 0) {
    prices[quantity - 1] += remainder;
  }

  return prices;
};

export const getBogoOriginPrice = (price: number, bogoInfo?: BogoInfo | null) => {
  // 기존 M1 50% 할인정책 대응
  if (bogoInfo && !bogoInfo?.discount_type) {
    const quantity = (bogoInfo?.required_quantity ?? 0) + 1;
    return price / quantity;
  }

  // MEMO: BE에서 BOGO 반영한 값(2개 옵션을 1개의 가격으로 처리하여)을 max_price로 내려주고 있어 BOGO일 경우 최종가에서 2로 나눠 중복 계산 방지.
  if (typeof bogoInfo?.required_quantity === 'number') {
    return price / 2;
  }

  return price;
};

type PriceList = { price: number; count: number }[];

export function getTotalInfo(items: SelectedItemMap) {
  const itemValues = Object.values(items);
  return itemValues.reduce(
    (acc, { is_zigzin, price, final_price, count }) => ({
      count: acc.count + count,
      price: acc.price + price * count,
      finalPrice: acc.finalPrice + final_price * count,
      priceList: acc.priceList.concat([{ price, count }]),
      generalPrice: acc.generalPrice + (is_zigzin ? 0 : price * count),
      zigzinPriceList: is_zigzin ? acc.zigzinPriceList.concat([{ price, count }]) : acc.zigzinPriceList,
      zigzinCount: acc.zigzinCount + (is_zigzin ? count : 0),
      zigzinPrice: acc.zigzinPrice + (is_zigzin ? price * count : 0),
    }),
    {
      count: 0,
      price: 0,
      finalPrice: 0,
      priceList: [] as PriceList,
      generalPrice: 0,
      zigzinPriceList: [] as PriceList,
      zigzinCount: 0,
      zigzinPrice: 0,
    },
  );
}

interface DiscountedPriceProps {
  priceList: PriceList;
  totalPrice: number;
  discountInfo?: MatchingProductDiscountInfo | ProductDiscountInfo | null;
  couponDiscountInfo?: ProductPriceCouponDiscountInfo | null;
  firstOrderDiscount?: FirstOrderDiscount | null;
  isCouponUnused?: boolean;
}

const PRODUCT_COUPON_TYPE = 'ORDER_ITEM';

export function getDiscountedPrice({
  priceList,
  totalPrice,
  couponDiscountInfo,
  firstOrderDiscount,
  isCouponUnused,
}: DiscountedPriceProps) {
  if (isCouponUnused) return totalPrice;

  if (firstOrderDiscount?.promotion_id) {
    return ceilPrice(getFirstOrderDiscountPrice(totalPrice, priceList, firstOrderDiscount));
  }

  const targetType = couponDiscountInfo?.target_type;
  const minRequiredAmount = couponDiscountInfo?.min_required_amount ?? 0;
  const isMoreThanCouponMinRequireAmount = totalPrice >= minRequiredAmount;

  if (targetType && isMoreThanCouponMinRequireAmount) {
    return ceilPrice(getCouponDiscountPrice(totalPrice, priceList, couponDiscountInfo));
  }
  return totalPrice;
}

function applyMaxDiscount(discountAmount: number, maxDiscountAmount: number | null) {
  if (!maxDiscountAmount) {
    return discountAmount;
  }
  return discountAmount > maxDiscountAmount ? maxDiscountAmount : discountAmount;
}

function getFirstOrderDiscountPrice(totalPrice: number, priceList: PriceList, firstOrderDiscount: FirstOrderDiscount) {
  if (firstOrderDiscount.discount_type === PdpCouponDiscountType.RATE) {
    const discountRateBp = firstOrderDiscount.discount_rate_bp ?? 0;
    const discountAmount = Math.max(...priceList.map((o) => o.price)) * bpToDecimal(discountRateBp);
    return getNonNegativeValue(totalPrice - discountAmount, totalPrice);
  }

  if (firstOrderDiscount.discount_type === PdpCouponDiscountType.AMOUNT) {
    return getNonNegativeValue(totalPrice - firstOrderDiscount.discount_amount, totalPrice);
  }

  return totalPrice;
}

function getCouponDiscountPrice(
  totalPrice: number,
  priceList: PriceList,
  couponDiscountInfo: ProductPriceCouponDiscountInfo | null,
) {
  let discountedPrice = 0;
  const targetType = couponDiscountInfo?.target_type;
  const discountType = couponDiscountInfo?.discount_type;
  const discountRate = bpToDecimal(couponDiscountInfo?.discount_rate_bp ?? 0);
  const discountAmountOfAmountCoupon = couponDiscountInfo?.discount_amount_of_amount_coupon ?? 0;
  const maxDiscountAmount = couponDiscountInfo?.max_discount_amount ?? null;

  if (discountType === PdpCouponDiscountType.RATE) {
    // MEMO: 정률 상품 그룹 쿠폰일 경우 가장 큰 금액에서 구한 할인 금액을 1회만 할인
    if (targetType === PRODUCT_COUPON_TYPE) {
      const discountAmount = ceilPrice(
        applyMaxDiscount(Math.max(...priceList.map((o) => o.price)) * discountRate, maxDiscountAmount),
      );
      discountedPrice = totalPrice - discountAmount;
    } else {
      const discountAmount = ceilPrice(totalPrice * discountRate);
      discountedPrice = totalPrice - applyMaxDiscount(discountAmount, maxDiscountAmount);
    }
  }

  if (discountType === PdpCouponDiscountType.AMOUNT) {
    const discountAmount = applyMaxDiscount(discountAmountOfAmountCoupon, maxDiscountAmount);
    discountedPrice = totalPrice - discountAmount;
  }
  return getNonNegativeValue(discountedPrice, totalPrice);
}

function getNonNegativeValue(value: number, defaultValue: number) {
  const isZeroOrNegative = value <= 0;
  return isZeroOrNegative ? defaultValue : value;
}

function ceilPrice(num: number) {
  return Math.ceil(num / 10) * 10;
}
